import {
  call,
  takeLatest,
  put,
  all,
  select,
  takeLeading,
} from 'redux-saga/effects';
import { isEmpty } from 'lodash';

import { getFilterVariables } from '@kizen/filters/variables';
import { searchEntityRecords } from '@kizen/api/records';
import AxiosService from 'services/AxiosService';

import { buildFilterSets } from '@kizen/filters/filter-sets';
import { setSearchParams } from 'hooks/useSearchParam';
import FieldService from 'services/FieldService';
import ClientService from 'services/ClientService';
import FilterGroupsService from 'services/FilterGroupsService';
import CustomObjectsService from 'services/CustomObjectsService';
import TeamMemberService, {
  RECORD_LIST_CONFIG_KEYS,
  collectRecordListPageConfig,
} from 'services/TeamMemberService';
import { camelToSnakeCaseKeys } from 'services/helpers';
import { FIELD_TYPES } from 'utility/constants';
import { generatePageConfig } from './contacts.redux';
import {
  buildPage as buildPageAction,
  getCounts as getCountsAction,
  getContacts as getContactsAction,
  createOrUpdateGroup as createOrUpdateGroupAction,
  changeGroup as changeGroupAction,
  setFilter as setFilterAction,
  resetFilter as resetFilterAction,
  removeGroup as removeGroupAction,
  setPageConfig as setPageConfigAction,
  setSavedFilter,
  buildPageComplete,
  buildPageFinish,
  buildPageFail,
  getContactsSuccess,
  getContactsStart,
  getContactsFail,
  getCountsSuccess,
  updateContact,
  createOrUpdateGroupSuccess,
  removeGroupSuccess,
  resetFilterGroupSuccess,
  createOrUpdateGroupFail,
  removeGroupFail,
  getCountsFail,
  updatePageConfigBySearch,
  setPageConfigSuccess,
  setPageConfigFail,
  setGroups,
  setGroup,
  getContactsFinish,
  updatePageConfig,
  clearFilterSearch,
  setCheckedContact,
  resetLastGeneratedFilter,
} from './actions';
import { enrichAndRepairQuickFilterSettings } from 'store/utilities';
import {
  buildPageFilterQuery,
  loadFilterFromUrlParam,
  loadSavedFilter,
} from 'ts-filters/utils';
import { anyFilterErrors, getFilterErrors } from 'hooks/useFilterErrorEffect';
import { selectFilterMetadataDefinition } from 'store/filterMetaData/selectors';
import { VIEW_VARIANTS } from 'components/ViewVariant/types';
import { GET_FILTER_GROUPS_PATHNAMES } from 'pages/FilterGroupsPage/constants';
import { TIMEZONE_OFFSET_HEADER } from 'services/constants';
import { getTimezoneOffset } from 'services/utils';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { setError } from 'store/errors';
import { selectColumnFieldIds, selectIsFilterFromUrl } from './selectors';

function* getContacts({ payload }) {
  let hasQuickFilters = false;
  let fetchedCriteria = null;

  try {
    yield put(getContactsStart(payload));

    const { isInitialPageLoad = false, onlyUpdateCounts } = payload;

    const pageConfig = yield select((s) => s.contactPage.pageConfig);
    const clientObject = yield select((s) => s.contactPage.clientObject);

    const allFields = yield select((s) => s.contactPage.allFields);
    const fieldIds = yield select(selectColumnFieldIds);
    const isFilterFromUrl = yield select(selectIsFilterFromUrl);

    if (!(isFilterFromUrl && isInitialPageLoad)) {
      const { criteria, isComplexFilters } = buildPageFilterQuery(
        pageConfig.filterObject,
        pageConfig.quickFilters,
        allFields,
        clientObject,
        pageConfig.quickFilterSettings
      );

      fetchedCriteria = criteria;
      hasQuickFilters = isComplexFilters;
    } else if (pageConfig.filterObject?.query) {
      fetchedCriteria = pageConfig.filterObject;
    }

    const body = {
      field_ids: onlyUpdateCounts ? [] : fieldIds,
    };

    const params = {
      page: onlyUpdateCounts ? 1 : pageConfig.page,
      page_size: onlyUpdateCounts ? 1 : pageConfig.size,
    };

    if (pageConfig.sort) {
      params.ordering = pageConfig.sort;
    }

    if (pageConfig.search) {
      params.search = pageConfig.search;
    }

    if (!pageConfig.filterObject.query && pageConfig.group) {
      params.group_id = pageConfig.group;
    }

    if (fetchedCriteria) {
      body.and = fetchedCriteria.and;
      body.query = camelToSnakeCaseKeys(fetchedCriteria.query);
    }

    const options = { body, params };

    if (fetchedCriteria) {
      options.headers = {
        [TIMEZONE_OFFSET_HEADER]: getTimezoneOffset(),
      };
    }

    const { queryFilter, ...withoutQueryFilter } = pageConfig;

    const [result] = yield all([
      call(searchEntityRecords, AxiosService, clientObject.id, options),
      payload.updatePageConfig &&
        call(
          TeamMemberService.updateCustomObjectRecordsListPageConfig,
          clientObject.id,
          collectRecordListPageConfig(
            withoutQueryFilter,
            payload.updatePageConfigKey || RECORD_LIST_CONFIG_KEYS.LAYOUT
          )
        ),
    ]);

    const hasNonFilterErrors = Boolean(result?.errors?.length);

    yield put(
      getContactsSuccess({
        contacts: hasNonFilterErrors || onlyUpdateCounts ? [] : result.results,
        contactsCount: hasNonFilterErrors ? 0 : result.count,
        refreshPage: payload.refreshPage,
        errors: null,
        fetchedCriteria,
      })
    );

    yield put(resetLastGeneratedFilter());
  } catch (error) {
    if (anyFilterErrors(error?.response, hasQuickFilters)) {
      const { count, results, errors } = getFilterErrors(
        error?.response?.data?.errors,
        hasQuickFilters
      );
      yield put(
        getContactsSuccess({
          contacts: results,
          contactsCount: count,
          refreshPage: payload.refreshPage,
          errors,
          fetchedCriteria,
        })
      );
    } else {
      yield put(getContactsFail(error));
    }
  } finally {
    yield put(getContactsFinish());
  }
}

function* maybeGetCounts() {
  const count = yield select((s) => s.contactPage.allCount);

  if (count === 0) {
    yield getCounts();
  }
}

function* getCounts() {
  try {
    const pageConfig = yield select((s) => s.contactPage.pageConfig);
    const [allCount, groupCount] = yield all([
      call(ClientService.count, {}),
      call(
        ClientService.count,
        { groupId: pageConfig.group },
        pageConfig.group
          ? {
              headers: {
                [TIMEZONE_OFFSET_HEADER]: getTimezoneOffset(),
              },
            }
          : {}
      ),
    ]);

    yield put(
      getCountsSuccess({
        allCount,
        groupCount: pageConfig.group ? groupCount : allCount,
      })
    );
  } catch (error) {
    yield put(getCountsFail(error));
  }
}

function* resetFilterGroup({ payload }) {
  const { groupId, history } = payload;

  const clientObject = yield select((s) => s.contactPage.clientObject);
  const pageConfig = yield select((s) => s.contactPage.pageConfig);

  setSearchParams(
    history,
    {
      group: null,
    },
    { method: 'replace' }
  );

  let reason;

  try {
    yield call(FilterGroupsService.getFilterGroup, clientObject.id, groupId);
    reason = 'hidden';
  } catch (error) {
    reason = error?.response?.status === 404 ? 'deleted' : 'not_allowed';
  }
  yield all([
    put(resetFilterGroupSuccess({ reason })),
    call(
      TeamMemberService.updateCustomObjectRecordsListPageConfig,
      clientObject.id,
      collectRecordListPageConfig(
        { ...pageConfig, group: null, filterObject: {} },
        RECORD_LIST_CONFIG_KEYS.LAYOUT
      )
    ),
  ]);
}

function* setFilter({ payload }) {
  const { filterObject } = payload;

  const filterObjectSnakeCase = camelToSnakeCaseKeys(filterObject);
  const model = yield select((s) => s.recordsPage.model);
  const access = yield select((s) => s.authentication.access);
  const vars = Object.fromEntries(getFilterVariables(model, { access }));
  const filterMetaData = yield select(selectFilterMetadataDefinition);

  const data = yield call(
    loadSavedFilter,
    filterMetaData,
    filterObjectSnakeCase,
    vars
  );
  yield put(updatePageConfig({ filterObject }));
  yield put(
    setSavedFilter({
      config: data,
      and: filterObject.and,
    })
  );
}

function* buildPage({ payload }) {
  try {
    const { page, history, clientObject: fetchedClientObject } = payload;

    const businessClientObject = yield select(getBusinessClientObject);
    let clientObject = fetchedClientObject;

    // If we already have the client object, we don't need to fetch it again
    if (!clientObject) {
      clientObject = yield call(FieldService.getModel, {
        id: businessClientObject.id,
      });
    }

    const [groupsResponse, { categorizedFields, fields }, pageResponse] =
      yield all([
        call(
          FilterGroupsService.getFilterGroups,
          clientObject.id,
          GET_FILTER_GROUPS_PATHNAMES.VISIBLE
        ), // /filter-groups/visible
        call(CustomObjectsService.getCategorizedModelFields, clientObject.id),
        call(
          TeamMemberService.getCustomObjectRecordsListPageConfig,
          clientObject.id,
          generatePageConfig()
        ),
      ]);

    const filterObjectSnakeCase = camelToSnakeCaseKeys(
      pageResponse.filterObject
    );

    const quickFilterSettings = yield call(
      enrichAndRepairQuickFilterSettings,
      pageResponse.quickFilterSettings,
      fields
    );

    yield put(
      buildPageComplete({
        clientObject,
        categorizedFields,
        fields,
        groupsResponse,
        pageResponse: {
          ...pageResponse,
          viewVariant: page.viewVariantOverride || pageResponse.viewVariant,
          filterObject: filterObjectSnakeCase,
          quickFilterSettings: quickFilterSettings.filter(Boolean),
        },
      })
    );

    const access = yield select((s) => s.authentication.access);
    const vars = Object.fromEntries(
      getFilterVariables(clientObject, { access })
    );

    const filterMetaData = yield select(selectFilterMetadataDefinition);

    if (page.queryFilter) {
      const data = yield call(
        loadFilterFromUrlParam,
        filterMetaData,
        page.queryFilter,
        vars
      );
      yield put(clearFilterSearch());
      yield put(setSavedFilter({ config: data, and: false }));
      yield put(
        updatePageConfig({
          filterObject: { and: false, query: buildFilterSets(data) },
        })
      );
    } else if (page.group) {
      const group = groupsResponse.find((g) => g.id === page.group);
      if (group) {
        const data = yield call(
          loadSavedFilter,
          filterMetaData,
          camelToSnakeCaseKeys(group.config),
          vars
        );
        yield put(updatePageConfig({ filterObject: group.config }));
        yield put(setSavedFilter({ config: data, and: group.config.and }));
      } else {
        // filter group can be deleted or hidden so we need to clear it from filter and page config
        yield resetFilterGroup({
          payload: { groupId: page.group, history },
        });
      }
    } else if (!isEmpty(pageResponse.filterObject)) {
      const group =
        pageResponse.group &&
        groupsResponse.find((g) => g.id === pageResponse.group);
      if ((pageResponse.group && group) || !pageResponse.group) {
        const data = yield call(
          loadSavedFilter,
          filterMetaData,
          filterObjectSnakeCase,
          vars
        );
        yield put(
          setSavedFilter({ config: data, and: pageResponse.filterObject.and })
        );
      } else {
        // filter group can be deleted or hidden so we need to clear it from filter and page config
        yield resetFilterGroup({
          payload: { groupId: pageResponse.group, history },
        });
      }
    }
    yield put(buildPageFinish());
  } catch (error) {
    yield put(buildPageFail(error));

    if (error.isAxiosError) {
      yield put(
        setError({
          text: error.toString(),
          code: error.response?.status ?? null,
          url: error.config.url,
        })
      );
    }
  }
}

function* createOrUpdateGroup({ payload }) {
  try {
    const { history, filterGroup, isPatch } = payload;
    const pageConfig = yield select((s) => s.contactPage.pageConfig);

    yield setSearchParams(history, {
      group: filterGroup.id,
      page: null,
    });

    yield put(
      createOrUpdateGroupSuccess({
        filterGroup,
        isPatch,
      })
    );

    yield put(
      updatePageConfig({
        page: 1,
        filterObject: filterGroup.config,
      })
    );

    yield getContacts({
      payload: {
        updatePageConfig: true,
        onlyUpdateCounts: pageConfig.viewVariant === VIEW_VARIANTS.CHARTS,
        updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
      },
    });
  } catch (error) {
    yield put(createOrUpdateGroupFail(error));
  }
}

function* removeGroup({ payload }) {
  try {
    const pageConfig = yield select((s) => s.contactPage.pageConfig);
    const clientObject = yield select((s) => s.contactPage.clientObject);
    yield call(
      FilterGroupsService.deleteFilterGroup,
      clientObject.id,
      pageConfig.group,
      pageConfig.group
    );
    yield setSearchParams(payload.history, {
      group: null,
      page: null,
    });
    yield put(removeGroupSuccess({ id: pageConfig.group }));

    yield getContacts({
      payload: {
        updatePageConfig: true,
        onlyUpdateCounts: pageConfig.viewVariant === VIEW_VARIANTS.CHARTS,
        updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
      },
    });
  } catch (error) {
    yield put(removeGroupFail(error));
  }
}

function* changeGroup({ payload }) {
  const { id, refetch } = payload;
  let groups = yield select((s) => s.contactPage.groups);
  const clientObject = yield select((s) => s.contactPage.clientObject);

  if (refetch) {
    const updatedGroups = yield call(
      FilterGroupsService.getFilterGroups,
      clientObject.id,
      GET_FILTER_GROUPS_PATHNAMES.VISIBLE
    );
    groups = updatedGroups;
    yield put(setGroups({ groups: updatedGroups }));
  }

  const pageConfig = yield select((s) => s.recordsPage.pageConfig);
  const group = groups.find((g) => g.id === id);

  yield put(setGroup(group ?? {}));
  yield put(updatePageConfig({ group: id, page: 1 }));
  yield put(getContactsStart());

  if (group && group.id) {
    const filterMetaData = yield select(selectFilterMetadataDefinition);
    const access = yield select((s) => s.authentication.access);
    const data = yield call(
      loadSavedFilter,
      filterMetaData,
      camelToSnakeCaseKeys(group.config),
      Object.fromEntries(getFilterVariables(clientObject, { access }))
    );
    yield put(setSavedFilter({ config: data, and: group.config.and }));
  } else {
    yield put(setSavedFilter({ config: null, and: false }));
    yield put(updatePageConfig({ filterObject: {} }));
  }

  yield getContacts({
    payload: {
      updatePageConfig: true,
      onlyUpdateCounts: pageConfig.viewVariant === VIEW_VARIANTS.CHARTS,
      updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
    },
  });
}

function* resetFilter() {
  const pageConfig = yield select((s) => s.contactPage.pageConfig);
  const filters = yield select((s) => s.contactPage.filters);

  if (filters.numberOfFilters && pageConfig?.filterObject?.query) {
    yield put(setSavedFilter({ config: null, and: false }));
    yield put(updatePageConfig({ filterObject: {} }));
    yield getContacts({
      payload: {
        updatePageConfig: true,
        onlyUpdateCounts: pageConfig.viewVariant === VIEW_VARIANTS.CHARTS,
        updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
      },
    });
  }
}

function* setPageConfig({ payload }) {
  try {
    const pageConfig = yield select((s) => s.contactPage.pageConfig);
    const clientObject = yield select((s) => s.contactPage.clientObject);
    yield call(
      TeamMemberService.updateCustomObjectRecordsListPageConfig,
      clientObject.id,
      collectRecordListPageConfig(
        pageConfig,
        payload?.updatePageConfigKey || RECORD_LIST_CONFIG_KEYS.LAYOUT
      )
    );
    yield put(setPageConfigSuccess());
  } catch (error) {
    yield put(setPageConfigFail(error));
  }
}

function* maybeRefreshFromPatch({ payload: { contact, updatedFieldIds } }) {
  if (updatedFieldIds) {
    const relationshipFieldUpdated = [...Object.values(contact.fields)].some(
      (fv) => {
        return (
          fv.field_type === FIELD_TYPES.Relationship.type &&
          updatedFieldIds.includes(fv.id)
        );
      }
    );

    if (relationshipFieldUpdated) {
      // This is a special case: updating a relationship field may update other
      // records appearing in the table (i.e. on the other side of the relationship).
      // We refresh the data on the current page as a best effort to show up-to-date
      // information.
      yield call(getContacts, {
        payload: { refreshPage: true, updatePageConfig: false },
      });
    }
  }
}

function* contactPage() {
  yield takeLatest(buildPageAction.type, buildPage);
  yield takeLatest(getCountsAction.type, getCounts);
  yield takeLatest(getContactsAction.type, getContacts);
  yield takeLatest(createOrUpdateGroupAction.type, createOrUpdateGroup);
  yield takeLatest(removeGroupAction.type, removeGroup);
  yield takeLatest(setPageConfigAction.type, setPageConfig);
  yield takeLatest(updatePageConfigBySearch.type, setPageConfig);
  yield takeLatest(updateContact.type, maybeRefreshFromPatch);
  yield takeLatest(changeGroupAction.type, changeGroup);
  yield takeLatest(resetFilterAction.type, resetFilter);
  yield takeLatest(setFilterAction.type, setFilter);
  yield takeLeading(setCheckedContact.type, maybeGetCounts);
}

export default contactPage;
